import { useMutationState, useQueryClient } from "@tanstack/react-query";
import { isReviewItemResolved } from "@utils/reportVisibility";

import {
  ResolveReportCommentMutationVariables,
  ResolveReportSuggestionMutationVariables,
  ReviewItemFieldsFragment,
  useResolveReportCommentMutation,
  useResolveReportSuggestionMutation,
} from "@api/graphql/types-and-hooks";
import { invalidateReviewItems, settleReviewMutation } from "@components/QaReview/hooks/reviewMutations";
import { useCloseThreadDrafts } from "@components/QaReview/hooks/useCloseThreadDrafts";
import { useGoToItem } from "@components/QaReview/hooks/useGoToItem";
import { useNavigationItems } from "@components/QaReview/hooks/useNavigationItems";
import { notifyError } from "@components/notifications/notifications";

/**
 * Picks, at click time, the open card after `item` in the navigator's order. The returned step closes
 * the item's drafts and goes there; call it only once the server confirms the item is closed.
 */
export const useAdvancePast = ({ advance = true }: { advance?: boolean } = {}) => {
  // The bar's own list, so Resolve's advance and the bar's Next agree on what comes after.
  const { items } = useNavigationItems();
  const goToItem = useGoToItem();
  const closeThreadDrafts = useCloseThreadDrafts();

  return (item: ReviewItemFieldsFragment) => {
    const index = items.findIndex((entry) => entry.id === item.id);
    // The list holds resolved cards while Resolved is picked, so step to the next open one, wrapping to the
    // top like the bar's Next.
    const ahead = index < 0 ? items : [...items.slice(index + 1), ...items.slice(0, index)];
    const next = ahead.find((entry) => !isReviewItemResolved(entry));
    return () => {
      closeThreadDrafts(item.id);
      if (next && advance) goToItem(next);
    };
  };
};

/** `advance: false` leaves the page where it is, for actions taken from a list that isn't the card stack. */
export const useResolveItem = (options: { advance?: boolean } = {}) => {
  const queryClient = useQueryClient();
  const advancePast = useAdvancePast(options);

  const resolveSuggestion = useResolveReportSuggestionMutation({
    onError: () => {
      notifyError("Error resolving suggestion. Please try again.");
      invalidateReviewItems(queryClient);
    },
    onSuccess: (data) => {
      const toasts = { failure: "Failed to resolve suggestion", success: "Suggestion resolved successfully" };
      if (settleReviewMutation(data?.resolveReportSuggestion, toasts)) invalidateReviewItems(queryClient);
    },
  });

  const resolveComment = useResolveReportCommentMutation({
    onError: () => notifyError("Error resolving comment. Please try again."),
    onSuccess: (data) => {
      const toasts = { failure: "Failed to resolve comment", success: "Comment resolved successfully" };
      if (settleReviewMutation(data?.resolveReportComment, toasts)) invalidateReviewItems(queryClient);
    },
  });

  const pendingSuggestionIds = useMutationState({
    filters: { mutationKey: ["resolveReportSuggestion"], status: "pending" },
    select: (mutation) => (mutation.state.variables as ResolveReportSuggestionMutationVariables).suggestionId,
  });
  const pendingCommentIds = useMutationState({
    filters: { mutationKey: ["resolveReportComment"], status: "pending" },
    select: (mutation) => (mutation.state.variables as ResolveReportCommentMutationVariables).id,
  });

  // Advance only once the server confirms the resolve — a failed resolve ("Comment is already
  // applied.", network error) must leave the navigator where it was.
  // `onResolved` runs once the server confirms, after the navigator moves; the caller must stay mounted until then.
  const resolve = (item: ReviewItemFieldsFragment, onResolved?: () => void) => {
    const step = advancePast(item);
    const advance = (success: boolean) => {
      if (!success) return;
      step();
      onResolved?.();
    };

    if (item.__typename === "ReportSuggestionType") {
      resolveSuggestion.mutate(
        { suggestionId: item.id },
        { onSuccess: (data) => advance(!!data?.resolveReportSuggestion?.success) },
      );
    } else {
      resolveComment.mutate({ id: item.id }, { onSuccess: (data) => advance(!!data?.resolveReportComment?.success) });
    }
  };

  const isResolving = (item: ReviewItemFieldsFragment) =>
    (item.__typename === "ReportSuggestionType" ? pendingSuggestionIds : pendingCommentIds).includes(item.id);

  return { resolve, isResolving };
};
